import React, { useState, useEffect } from 'react';
import { ProductFormPopupStandalone } from './WidgetTypes/ProductFormPopup';
import { database } from '../firebaseConfig';
import { ref, onValue } from 'firebase/database';

/**
 * PopupManager - Globales System für alle Popups im Page Builder
 * 
 * Features:
 * - Verwaltet Product Form Popups außerhalb des normalen Widget-Systems
 * - Ermöglicht Button-Widgets das Triggern von Produktformularen
 * - Bietet Preview-Funktionalität für Popup-Styling
 */
const PopupManager = ({ 
    products = [], 
    isEditMode = false, 
    onSubmitForm,
    popupStyling = {},
    pageId = '' // Page ID für pro-Page Styling
}) => {
    const [activePopup, setActivePopup] = useState(null);
    const [popupConfig, setPopupConfig] = useState({});
    const [liveCustomStyling, setLiveCustomStyling] = useState({});
    const [pagePopupStyling, setPagePopupStyling] = useState({});

    // Load page-specific popup styling
    useEffect(() => {
        if (!pageId) return;

        const stylingRef = ref(database, `pages/${pageId}/popupStyling`);
        
        const unsubscribe = onValue(stylingRef, (snapshot) => {
            const styling = snapshot.val() || {};
            console.log('🎨 PopupManager: Loaded page styling for pageId:', pageId);
            console.log('🎨 PopupManager: Styling data:', styling);
            console.log('🎨 PopupManager: isEditMode:', isEditMode);
            setPagePopupStyling(styling);
        });

        return () => unsubscribe();
    }, [pageId]);

    // Listen for popup trigger events
    useEffect(() => {
        const handlePopupTrigger = (event) => {
            const { productId, trigger, customStyling = {}, productData: passedProductData } = event.detail;
            
            console.log('🔄 PopupManager: Popup trigger received');
            console.log('🔄 PopupManager: Product ID:', productId);
            console.log('🔄 PopupManager: Available products:', products.length);
            console.log('🔄 PopupManager: Passed product data:', passedProductData);
            
            // Try to find product data in the products array first
            let productData = products.find(p => p.id === productId);
            
            // If not found in products array, use passed product data
            if (!productData && passedProductData) {
                console.log('🔄 PopupManager: Using passed product data');
                productData = passedProductData;
            }
            
            if (!productData) {
                console.error(`❌ PopupManager: Product not found: ${productId}`);
                console.log('Available product IDs:', products.map(p => p.id));
                return;
            }

            console.log('✅ PopupManager: Product data found:', productData.title);

            // Configure popup with page-specific styling
            const mergedStyling = { ...popupStyling, ...pagePopupStyling, ...customStyling };
            console.log('🎨 PopupManager: Merged styling for popup:', mergedStyling);
            console.log('🎨 PopupManager: pagePopupStyling:', pagePopupStyling);
            console.log('🎨 PopupManager: customStyling from trigger:', customStyling);
            
            setPopupConfig({
                productData,
                trigger,
                customStyling: mergedStyling
            });
            setActivePopup('productForm');
        };

        const handleTestPopupTrigger = (event) => {
            const { productTitle = 'Sample Digital Product', trigger } = event.detail;
            
            // Create test product data
            const testProductData = {
                id: 'test-product',
                title: productTitle,
                description: 'This is a preview of how the form popup will look.',
                formFields: [
                    { name: 'name', label: 'Full Name', type: 'text', required: true, placeholder: 'Enter your full name' },
                    { name: 'email', label: 'Email Address', type: 'email', required: true, placeholder: 'Enter your email address' }
                ]
            };

            setPopupConfig({
                productData: testProductData,
                trigger,
                customStyling: { ...popupStyling, ...pagePopupStyling }
            });
            setActivePopup('productForm');
        };

        // Live styling update handler
        const handleLiveStylingUpdate = (event) => {
            const { styling } = event.detail;
            console.log('🎨 PopupManager: Live styling update received:', styling);
            setLiveCustomStyling(styling);
        };

        // Add event listeners
        window.addEventListener('triggerProductFormPopup', handlePopupTrigger);
        window.addEventListener('testFormPopup', handleTestPopupTrigger);
        window.addEventListener('updatePopupStyling', handleLiveStylingUpdate);

        return () => {
            window.removeEventListener('triggerProductFormPopup', handlePopupTrigger);
            window.removeEventListener('testFormPopup', handleTestPopupTrigger);
            window.removeEventListener('updatePopupStyling', handleLiveStylingUpdate);
        };
    }, [products, popupStyling]);

    const closePopup = () => {
        setActivePopup(null);
        setPopupConfig({});
    };

    const handleFormSubmit = async (formData) => {
        console.log('🔄 PopupManager: Form submission started');
        console.log('🔄 PopupManager: Form data:', formData);
        console.log('🔄 PopupManager: Product data:', popupConfig.productData);
        console.log('🔄 PopupManager: onSubmitForm exists:', !!onSubmitForm);
        console.log('🔄 PopupManager: isEditMode:', isEditMode);
        
        try {
            if (onSubmitForm) {
                console.log('🔄 PopupManager: Calling onSubmitForm...');
                await onSubmitForm(formData, popupConfig.productData);
                console.log('✅ PopupManager: onSubmitForm completed successfully');
            } else {
                console.warn('⚠️ PopupManager: No onSubmitForm handler provided');
            }
            closePopup();
        } catch (error) {
            console.error('❌ PopupManager: Form submission error:', error);
            console.error('❌ PopupManager: Error details:', error.message);
            console.error('❌ PopupManager: Error stack:', error.stack);
            // Don't close popup on error, let user retry
        }
    };

    // Render active popup
    if (activePopup === 'productForm' && popupConfig.productData) {
        // Merge original styling with live updates
        const mergedStyling = {
            ...popupConfig.customStyling,
            ...liveCustomStyling
        };
        
        return (
            <ProductFormPopupStandalone
                isOpen={true}
                onClose={closePopup}
                productData={popupConfig.productData}
                customStyling={mergedStyling}
                isPreviewMode={isEditMode}
                onSubmit={handleFormSubmit}
            />
        );
    }

    return null;
};

export default PopupManager;

/**
 * Helper functions for triggering popups from widgets
 */
export const triggerProductFormPopup = (productId, customStyling = {}) => {
    const event = new CustomEvent('triggerProductFormPopup', {
        detail: { 
            productId, 
            trigger: 'widget',
            customStyling 
        }
    });
    window.dispatchEvent(event);
};

export const triggerTestFormPopup = (productTitle = 'Sample Digital Product') => {
    const event = new CustomEvent('testFormPopup', {
        detail: { 
            productTitle,
            trigger: 'test'
        }
    });
    window.dispatchEvent(event);
};
